import { useAuth } from "../context/AuthContext";
import { Button } from "../components/common/Button";
import styles from "./LoginScreen.module.css";

/**
 * Shown when the API returns a 401 that signing in again cannot fix (#166).
 *
 * Before this existed, every 401 called logout(), which redirects to the
 * Microsoft sign-out page. For a valid token with no app.Users row that
 * produced a loop with no exit and no message -- sign in, bounce out, sign
 * in -- and the redirect wiped the console and network log on the way past,
 * so even diagnosing it meant knowing to tick "Preserve log" first. It cost
 * real hours twice.
 *
 * The rule this screen embodies: a dead end must be VISIBLE and must stay
 * on screen. Sign Out is offered because switching accounts is the one
 * thing that might genuinely help, but it is the user's choice, not
 * something that happens to them.
 */
const COPY = {
  not_provisioned: {
    icon: "🚪",
    title: "This account isn't set up yet",
    // Names both halves, because "contact your administrator" with no
    // detail is what makes support tickets take three round trips.
    body:
      "You signed in successfully, but there's no A Beekeepers Chronicle account for this " +
      "email address yet. Signing in again won't change that — someone with " +
      "admin access needs to create the account first.",
    hint: "If you were expecting access, send them the email address you just used.",
  },
  suspended: {
    icon: "🚫",
    title: "This account has been suspended",
    // Deliberately does NOT say why. The reason is between the person who
    // suspended the account and the person it belongs to, and guessing at
    // it here would either leak a moderation decision or invent one.
    body:
      "Your sign-in worked, but this A Beekeepers Chronicle account has been suspended. " +
      "Signing in again won't change that, and a new account won't be created " +
      "for you.",
    hint: "If you think this is a mistake, contact whoever administers your apiary.",
  },
  misconfigured: {
    icon: "🔧",
    title: "A Beekeepers Chronicle is misconfigured",
    body:
      "Your sign-in worked, but the app couldn't accept the result. This is a " +
      "setup problem on our side, not something you did wrong — signing in " +
      "again won't help.",
    hint: "Please report this, along with the detail below and roughly what time it happened.",
  },
};

export function AuthBlockedScreen() {
  const { authBlock, logout, clearAuthBlock } = useAuth();
  const copy = COPY[authBlock?.kind] ?? COPY.misconfigured;

  const signOut = () => {
    // Clear first: if the redirect is slow, the user should not be staring
    // at the dead end while it happens.
    clearAuthBlock();
    logout();
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.card} role="alert">
        <div className={styles.logo} aria-hidden="true">{copy.icon}</div>
        <h1>{copy.title}</h1>
        <p>{copy.body}</p>
        <p style={{ color: "var(--color-text-muted)", fontSize: "0.9rem" }}>{copy.hint}</p>
        {/* The server's own message. Kept verbatim and visually secondary:
            it is the string an operator needs, and paraphrasing it would
            defeat the point. */}
        {authBlock?.message ? (
          <p
            style={{
              marginTop: "var(--space-3)",
              padding: "var(--space-2)",
              background: "var(--color-surface-alt, rgba(0,0,0,0.04))",
              borderRadius: 6,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: "0.85rem",
              wordBreak: "break-word",
            }}
          >
            {authBlock.message}
          </p>
        ) : null}
        <div style={{ marginTop: "var(--space-4)" }}>
          <Button variant="secondary" onClick={signOut}>Sign Out</Button>
        </div>
      </div>
    </div>
  );
}
